import { useEffect, useRef, useState } from 'react'

type LazyVideoProps = {
  src: string
  className?: string
}

const LazyVideo = ({ src, className }: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const [shouldLoad, setShouldLoad] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [isPlaying, setIsPlaying] = useState(true)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    if (videoRef.current) observer.observe(videoRef.current)

    return () => observer.disconnect()
  }, [])

  function toggleMute() {
    if (!videoRef.current) return

    const nextMuted = !isMuted
    videoRef.current.muted = nextMuted
    setIsMuted(nextMuted)
  }

  function togglePlay() {
    if (!videoRef.current) return

    if (videoRef.current.paused) {
      videoRef.current.play()
      setIsPlaying(true)
    } else {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }

  return (
    <div className='relative'>
      <video ref={videoRef} autoPlay muted loop playsInline preload='none' className={className}>
        {shouldLoad && <source src={src} type='video/mp4' />}
      </video>

      <div className='absolute -bottom-15 sm:bottom-5 right-0 sm:right-4 flex gap-2 z-10'>
        <button
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
          className='rounded-full text-3xl px-4 py-2 text-white cursor-pointer shadow border bg-black/70'
        >
          {isPlaying ? '⏸' : '▶️'}
        </button>
        <button
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          className={`rounded-full text-3xl px-4 py-2 text-white cursor-pointer shadow border ${
            isMuted ? 'bg-green-500' : 'bg-blue-800'
          }`}
        >
          {isMuted ? '🔊' : '🔇'}
        </button>
      </div>
    </div>
  )
}

export default LazyVideo
